import { FormEvent, useEffect, useId, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { subscribeNewsletter } from '@/api/clients'
import { track } from '@/api/tracking'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { ROUTES } from '@/navigation/constants'
import { localizePath } from '@/navigation/locale'
import { useLandingLocale } from '@/navigation/useLandingLocale'
import { type NewsletterSubscribeVariant, isValidNewsletterEmail } from '@tipi/shared'

import { newsletterCopy, resolveNewsletterCta } from './newsletterCopy'

type FormStatus = 'idle' | 'loading' | 'success' | 'error'

type FormError = 'email' | 'consent' | 'server'

type SubscribeFormProps = {
    variant: NewsletterSubscribeVariant
    source: string
    className?: string
}

const FORM_ERROR_COPY: Record<FormError, string> = {
    email: newsletterCopy.errorEmail,
    consent: newsletterCopy.errorConsent,
    server: newsletterCopy.errorServer,
}

export function SubscribeForm({ variant, source, className }: SubscribeFormProps) {
    const locale = useLandingLocale()
    const inputId = useId()
    const msgId = useId()
    const successRef = useRef<HTMLDivElement>(null)

    const [email, setEmail] = useState('')
    const [consent, setConsent] = useState(false)
    const [honeypot, setHoneypot] = useState('')
    const [status, setStatus] = useState<FormStatus>('idle')
    const [error, setError] = useState<FormError | null>(null)

    const loading = status === 'loading'
    const showForm = status === 'idle' || status === 'loading' || status === 'error'
    const focusRingClass = variant === 'article' ? 'focus:shadow-focus-paper' : 'focus:shadow-focus-cream'

    const errorText = error ? FORM_ERROR_COPY[error] : undefined

    useEffect(() => {
        if (status !== 'success') return
        successRef.current?.focus()
    }, [status])

    const reset = () => {
        setEmail('')
        setConsent(false)
        setStatus('idle')
        setError(null)
    }

    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (loading) return

        const trimmed = email.trim()
        if (!isValidNewsletterEmail(trimmed)) {
            setStatus('error')
            setError('email')
            return
        }
        if (!consent) {
            setStatus('error')
            setError('consent')
            return
        }

        setStatus('loading')
        setError(null)
        track('newsletter_submit', { source, variant })

        try {
            const result = await subscribeNewsletter({
                email: trimmed,
                consent: true,
                source,
                variant,
                company: honeypot,
            })

            setStatus('success')
            track('newsletter_success', {
                source,
                variant,
                result: result.status,
            })
        } catch {
            setStatus('error')
            setError('server')
        }
    }

    if (!showForm) {
        return (
            <div
                ref={successRef}
                tabIndex={-1}
                role='status'
                className={cn('flex flex-wrap items-center gap-[18px] py-1 outline-none', className)}
            >
                <img
                    src='/newsletter/mascota-jumping.png'
                    alt=''
                    width={72}
                    height={78}
                    className='h-[78px] w-[72px] shrink-0 object-contain'
                />
                <div className='flex min-w-[240px] flex-1 flex-col gap-1.5'>
                    <p className='m-0 text-xs font-bold uppercase tracking-caps text-flame-press'>
                        {newsletterCopy.successEyebrow}
                    </p>
                    <p className='m-0 text-[22px] font-extrabold leading-[27px] tracking-tight text-ink'>
                        {newsletterCopy.successTitle}
                    </p>
                    <p className='m-0 text-pretty text-[15px] leading-[22px] text-ink-2'>
                        {newsletterCopy.successBodyBefore}
                        <strong className='font-bold text-ink'>{email.trim()}</strong>
                        {newsletterCopy.successBodyAfter}
                    </p>
                    <button
                        type='button'
                        onClick={reset}
                        className='mt-1 min-h-11 cursor-pointer self-start border-0 bg-transparent p-0 text-sm font-semibold text-ink underline'
                    >
                        {newsletterCopy.useAnotherEmail}
                    </button>
                </div>
            </div>
        )
    }

    return (
        <form
            onSubmit={onSubmit}
            noValidate
            aria-label='Suscripción a Novedades Tipi'
            className={cn('m-0 flex flex-col gap-3', className)}
        >
            <label htmlFor={inputId} className='text-sm font-semibold leading-[18px] text-ink'>
                {newsletterCopy.emailLabel}
            </label>

            <input
                id={inputId}
                type='email'
                name='email'
                autoComplete='email'
                inputMode='email'
                required
                placeholder={newsletterCopy.emailPlaceholder}
                value={email}
                disabled={loading}
                aria-invalid={error === 'email'}
                aria-describedby={error === 'email' || error === 'server' ? msgId : undefined}
                onChange={event => {
                    setEmail(event.target.value)
                    if (error === 'email') setError(null)
                }}
                className={cn(
                    'h-[52px] w-full rounded-field border-[1.5px] bg-field px-4 text-base text-ink outline-none transition-[box-shadow,border-color] duration-150 ease-tipi',
                    error === 'email' ? 'border-flame-press' : 'border-field-border focus:border-ink',
                    focusRingClass,
                )}
            />

            <label className='mt-0.5 flex cursor-pointer items-start gap-2.5'>
                <input
                    type='checkbox'
                    name='consent'
                    required
                    checked={consent}
                    disabled={loading}
                    aria-invalid={error === 'consent'}
                    aria-describedby={error === 'consent' ? msgId : undefined}
                    onChange={event => {
                        setConsent(event.target.checked)
                        if (error === 'consent') setError(null)
                    }}
                    className='mt-0 size-5 shrink-0 cursor-pointer accent-flame-press'
                />
                <span className='text-sm leading-5 text-ink'>{newsletterCopy.consent}</span>
            </label>

            <p className='m-0 text-pretty text-[13px] leading-[19px] text-ink-2'>
                {newsletterCopy.legalBeforeLink}
                <Link to={localizePath(ROUTES.privacyPolicy, locale)} className='font-semibold text-ink underline'>
                    {newsletterCopy.legalLink}
                </Link>
                {newsletterCopy.legalAfterLink}
            </p>

            {errorText ? (
                <p id={msgId} role='alert' className='m-0 text-sm font-semibold leading-5 text-flame-press'>
                    {errorText}
                </p>
            ) : null}

            <Button
                type='submit'
                variant='flame'
                disabled={loading}
                aria-busy={loading}
                className={cn('min-h-[52px] w-full px-5 text-base', loading && 'cursor-progress opacity-80')}
            >
                {loading ? newsletterCopy.ctaLoading : resolveNewsletterCta(variant)}
            </Button>

            <input
                type='text'
                name='company'
                value={honeypot}
                onChange={event => setHoneypot(event.target.value)}
                tabIndex={-1}
                autoComplete='off'
                aria-hidden
                className='absolute -left-[9999px] h-0 w-0 opacity-0'
            />
        </form>
    )
}
